import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faStar as StarRegular } from '@fortawesome/free-regular-svg-icons';
import {
    faBackwardStep,
    faBoxArchive,
    faCalendarPlus,
    faComment,
    faCommentSms,
    faEnvelope,
    faForwardStep,
    faHourglass,
    faHourglassEnd,
    faPaperclip,
    faPlay,
    faStar,
    faTrash,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FolderActionType } from 'src/app/models/enums/folder-action-type';
import { NavigateType } from 'src/app/models/enums/navigate-type';
import { Rights } from 'src/app/models/server/enums/rights';
import { ServiceCodes } from 'src/app/models/server/enums/service-codes';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';

@Component({
    selector: 'app-folder-detail-menu',
    templateUrl: './folder-detail-menu.component.html',
    styleUrls: ['./folder-detail-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButtonModule, MatTooltipModule, FaIconComponent, MatIconModule]
})
export class FolderDetailMenuComponent {
    /**
     * Références des icônes utilisés dans le template.
     */
    public nextIcon = faPlay;
    public firstIcon = faBackwardStep;
    public lastIcon = faForwardStep;
    public attachmentIcon = faPaperclip;
    public emailIcon = faEnvelope;
    public smsIcon = faCommentSms;
    public commentIcon = faComment;
    public standByIcon = faHourglass;
    public endStandByIcon = faHourglassEnd;
    public addReminderIcon = faCalendarPlus;
    public trashIcon = faTrash;
    public archivingIcon = faBoxArchive;
    public waitingArchivingIcon = faBoxArchive;
    public nonWaitingArchivingIcon = faBoxArchive;
    public warningIcon = faTriangleExclamation;
    public followFolderIcon = faStar;
    public stopFollowFolderIcon = StarRegular;

    /**
     * Références des Enums utilisées dans le template.
     */
    public NavigateType = NavigateType;
    public Rights = Rights;

    /**
     * Valeur indiquant si le composant est affiché en mode consultation.
     */
    @Input()
    public readonly: boolean;

    /**
     * Valeur indiquant si le dossier est en veille.
     */
    @Input()
    public isInStandbyState: boolean;

    /**
     * Valeur indiquant si le dossier est dans l'état 'en attente d'archivage'.
     */
    @Input()
    public isInWaitingArchivingState: boolean;

    /**
     * Valeur indiquant si le dossier possède des warnings.
     */
    @Input()
    public hasWarning: boolean;

    /**
     * Valeur indiquant si le dossier est suivi.
     */
    @Input()
    public isFollowed: boolean;

    /**
     * Emis lorsqu'une action sur le dossier est demandée.
     */
    @Output()
    public actionRequested: EventEmitter<FolderActionType> = new EventEmitter();

    /**
     * Emis lorsqu'une navigation entre dossiers est demandée.
     */
    @Output()
    public navigationRequested: EventEmitter<NavigateType> = new EventEmitter();

    /**
     * Droits de l'utilisateur.
     */
    public userRights = new Map<Rights, boolean>();

    /**
     * Constructeur.
     */
    constructor(private connectedUserStateService: ConnectedUsersStateService) {
        this.RetrieveUserRights();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Ajouter un commentaire'.
     */
    public onAddComment(): void {
        this.actionRequested.emit(FolderActionType.AddComment);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Ajouter un fichier'.
     */
    public onAddFile(): void {
        this.actionRequested.emit(FolderActionType.AddFile);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Envoyer un mail'.
     */
    public onSendEmail(): void {
        this.actionRequested.emit(FolderActionType.SendEmail);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Envoyer un SMS'.
     */
    public onSendSms(): void {
        this.actionRequested.emit(FolderActionType.SendSms);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur un des boutons de navigation.
     */
    public onNavigate(type: NavigateType): void {
        this.navigationRequested.emit(type);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Supprimer ce dossier'.
     */
    public onDelete(): void {
        this.actionRequested.emit(FolderActionType.Delete);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Brouillons de mail en attente'.
     */
    public onDisplayDraftEmail(): void {
        this.actionRequested.emit(FolderActionType.DisplayDraftEmails);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Mettre le dossier en veille'.
     */
    public setInStandByState(): void {
        this.actionRequested.emit(FolderActionType.SetInStandByState);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Sortir le dossier de veille'.
     */
    public removeFromStandByState(): void {
        this.actionRequested.emit(FolderActionType.RemoveFromStandByState);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Placer le dossier en attente d'archivage'.
     */
    public setInWaitingArchivingState(): void {
        this.actionRequested.emit(FolderActionType.SetInWaitingArchivingState);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Retirer le dossier de l'état en attente d'archivage'.
     */
    public removeFromWaitingArchivingState(): void {
        this.actionRequested.emit(FolderActionType.RemoveFromWaitingArchivingState);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Ajouter un rappel'.
     */
    public onAddReminder(): void {
        this.actionRequested.emit(FolderActionType.AddReminder);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Archiver le dossier'.
     */
    public onArchiving(): void {
        this.actionRequested.emit(FolderActionType.ArchiveFolder);
    }

    public followFolder(follow: boolean) {
        if (follow === true) {
            this.actionRequested.emit(FolderActionType.FollowFolder);
        } else {
            this.actionRequested.emit(FolderActionType.StopFollowFolder);
        }
    }

    /**
     * Récupère les droits de l'utilisateur utilisés par le composant.
     */
    private RetrieveUserRights() {
        this.userRights.set(Rights.AddComment, this.connectedUserStateService.hasRight(Rights.AddComment));
        this.userRights.set(Rights.AddFileToFolder, this.connectedUserStateService.hasRight(Rights.AddFileToFolder));
        this.userRights.set(Rights.SendEmail, this.connectedUserStateService.hasRight(Rights.SendEmail));
        this.userRights.set(
            Rights.SendSms,
            this.connectedUserStateService.hasRight(Rights.SendSms) && this.connectedUserStateService.hasServiceSubscription(ServiceCodes.HubSmsSending)
        );
        this.userRights.set(Rights.FolderDelete, this.connectedUserStateService.hasRight(Rights.FolderDelete));
        this.userRights.set(Rights.ChangeStandByState, this.connectedUserStateService.hasRight(Rights.ChangeStandByState));
        this.userRights.set(Rights.ReminderCreate, this.connectedUserStateService.hasRight(Rights.ReminderCreate));
        this.userRights.set(Rights.ArchiveCreate, this.connectedUserStateService.hasRight(Rights.ArchiveCreate));
        this.userRights.set(Rights.FollowFolder, this.connectedUserStateService.hasRight(Rights.FollowFolder));
    }
}
